import { Node, mergeAttributes, nodePasteRule } from "@tiptap/core";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    tweetEmbed: {
      setTweetEmbed: (options: { tweetId: string }) => ReturnType;
    };
  }
}

/**
 * Extracts the tweet ID from a Twitter/X URL.
 * Supports: twitter.com/user/status/ID, x.com/user/status/ID
 */
function getTweetId(url: string): string | null {
  const match = url.match(/(?:twitter\.com|x\.com)\/\w+\/status\/(\d+)/);
  return match ? match[1] : null;
}

/** Matches Twitter and X.com status URLs for paste rules. */
const TWEET_URL_REGEX =
  /(?:https?:\/\/)?(?:www\.)?(?:twitter\.com|x\.com)\/\w+\/status\/\d+\S*/g;

export const TweetEmbed = Node.create({
  name: "tweetEmbed",

  group: "block",

  atom: true,

  addAttributes() {
    return {
      tweetId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-tweet-id"),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-tweet-id]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const tweetId = HTMLAttributes.tweetId as string;
    const tweetUrl = `https://x.com/i/status/${tweetId}`;

    return [
      "div",
      mergeAttributes(
        {
          "data-tweet-id": tweetId,
          class: "tweet-embed",
        },
        // Don't pass tweetId as an HTML attribute on the div (already set above)
        { ...HTMLAttributes, tweetId: undefined },
      ),
      [
        "a",
        {
          href: tweetUrl,
          target: "_blank",
          rel: "noopener noreferrer",
        },
        "View tweet",
      ],
    ];
  },

  addCommands() {
    return {
      setTweetEmbed:
        ({ tweetId }) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { tweetId },
          });
        },
    };
  },

  addPasteRules() {
    return [
      nodePasteRule({
        find: TWEET_URL_REGEX,
        type: this.type,
        getAttributes: (match) => {
          const url = match[0];
          const tweetId = getTweetId(url);
          if (!tweetId) return false;
          return { tweetId };
        },
      }),
    ];
  },
});
